import { useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { SidebarMenu, type SidebarMenuItem } from '@/components/SidebarMenu';
import { SidePanel } from '@/components/SidePanel';
import { OrganizationTab } from './components/OrganizationTab';
import ConfigurationTab from './components/ConfigurationTab';
import { MembersTab } from './components/MembersTab';
import { LogsTab } from './components/LogsTab';
import { StorageTab } from './components/StorageTab';
import { IntegrationsTab } from './components/IntegrationsTab';
import { LicenseTab } from './components/LicenseTab';
import { SettingsIcon } from 'lucide-react';

const SECTION_IDS = [
    'general',
    'configuration',
    'members',
    'logs',
    'storage',
    'integrations',
    'license',
] as const;

type SettingsSection = (typeof SECTION_IDS)[number];

const SECTION_COMPONENTS: Record<SettingsSection, React.ComponentType> = {
    general: OrganizationTab,
    configuration: ConfigurationTab,
    members: MembersTab,
    logs: LogsTab,
    storage: StorageTab,
    integrations: IntegrationsTab,
    license: LicenseTab,
};

const DEFAULT_SECTION: SettingsSection = SECTION_IDS[0];

function isSection(value: string | undefined): value is SettingsSection {
    return (SECTION_IDS as readonly string[]).includes(value ?? '');
}

export function SettingsView() {
    const { t } = useTranslation(I18nNamespace.SETTINGS);
    const navigate = useNavigate();
    const { section } = useParams<{ section?: string }>();
    const activeSection: SettingsSection = isSection(section)
        ? section
        : DEFAULT_SECTION;

    useEffect(() => {
        if (section && !isSection(section)) {
            navigate(`/settings/${DEFAULT_SECTION}`, { replace: true });
        }
    }, [section, navigate]);

    const sectionItems: SidebarMenuItem[] = SECTION_IDS.map((id) => ({
        id,
        label: t(`tabs.${id}`),
    }));

    const goToSection = (id: string) => navigate(`/settings/${id}`);
    const ActiveComponent = SECTION_COMPONENTS[activeSection];

    return (
        <div className="relative h-full min-h-0 flex flex-col">
            <SidePanel side="left">
                <SidebarMenu
                    items={sectionItems}
                    activeId={activeSection}
                    onItemClick={goToSection}
                    header={{
                        title: t('title'),
                        icon: SettingsIcon,
                    }}
                />
            </SidePanel>
            <div
                className="flex-1 min-h-0 overflow-y-auto scrollbar-hide px-4 panels:px-8 py-6"
                data-testid="settings-view"
            >
                <div className="max-w-content mx-auto w-full">
                    <ActiveComponent />
                </div>
            </div>
        </div>
    );
}
